import { DOCUMENT } from '@angular/common';
import { inject, Injectable, NgZone } from '@angular/core';
import { ScriptsService } from '@roller/rdl/utilities/scripts';
import { BehaviorSubject, filter, take, tap } from 'rxjs';
import { CRouteList } from '@app/route-list.const';
import { AppConfigService } from '@core/app-config/app-config.service';
import { InitialConfigService } from '@core/app-config/initial-config.service';
import { IDiscounts } from '@core/discounts/models/discounts.model';
import { IframeService } from '@core/iframe/iframe.service';
import { LoggerService } from '@core/logger/logger.service';
import { IOrderItem } from '@core/order/models/order-item.model';
import { IOrder } from '@core/order/models/order.model';
import { IProduct } from '@core/product/models/product.model';
import { CStorageKeys } from '@core/storage/storage-keys.const';
import { StorageService } from '@core/storage/storage.service';
import { IVenueConfig } from '@core/venue-config/models/venue-config.model';
import { IDictionary } from '../common.model';
import { AnalyticsMappingService } from './analytics-mapping.service';
import { IAnalyticsWindow, ITracking } from './analytics_item.model';
import {
  IMatomoEcommerceItem,
  IMatomoEcommerceOrder,
  IMatomoSetCustomDimension,
  IMatomoSite,
  TMatomoEvents
} from './matomo-analytics.model';
import { MatomoAbTestingService } from './matomo-abtesting.service';
@Injectable({
  providedIn: 'root'
})
export class MatomoAnalyticsService {
  private document = inject(DOCUMENT);
  private zone = inject(NgZone);
  private appConfigService = inject(AppConfigService);
  private storageService = inject(StorageService);
  private analyticsMapping = inject(AnalyticsMappingService);
  private scriptsService = inject(ScriptsService);
  private iframeService = inject(IframeService);
  private initialConfigService = inject(InitialConfigService);
  private loggerService = inject(LoggerService);
  private matomoAbTestingService = inject(MatomoAbTestingService);

  readonly cRoutes = CRouteList;

  isInitialized$ = new BehaviorSubject<boolean>(false);

  private window: IAnalyticsWindow = this.document.defaultView as Window as IAnalyticsWindow;
  private matomoSite?: IMatomoSite;
  private venueSlug = '';
  private checkoutSlug = '';
  private isInIframe = false;

  matomo: ITracking = {
    isEnabled: false,
    isScriptAdded: false
  };

  setInitialized(value: boolean) {
    this.isInitialized$.next(value);
  }

  getInitialized() {
    return this.isInitialized$.asObservable();
  }

  bootstrap(venueConfig: IVenueConfig, checkoutSlug: string): void {
    if (!venueConfig.analytics?.matomoAnalyticsEnabled || this.isInitialized$.value) {
      this.setInitialized(true);
      return;
    }

    try {
      this.venueSlug = this.storageService.getItem<string>(CStorageKeys.apiKey, false) as string;
      this.checkoutSlug = checkoutSlug;
      this.isInIframe = this.iframeService.getIsInsideIframe();
      this.setMatomoSite(venueConfig);
      this.window.matomoLoaded = () => this.bootstrapAbTesting(venueConfig);
      this.loadMatomoScripts();
    } catch (error: unknown) {
      this.handleMatomoAnalyticsError(error as Error);
    }
  }

  trackPageView(page_location: string, page_path: string | undefined): void {
    page_path ??= this.window?.location.pathname;
    if (page_path) {
      const cleanPath = page_path.split('?')[0].toLowerCase();
      this.callMatomo('setCustomUrl', { cleanPath });
    }

    this.callMatomo('setDocumentTitle', { page_location });
    this.callMatomo('trackPageView');
    this.resetEcommerceView();
  }

  trackEcommerceView(item: IProduct, category: string) {
    this.callMatomo('setEcommerceView', this.analyticsMapping.mapProductToMatomoSetEcommerceViewItem(item, category));
  }

  /** Handles adding and removing items to cart and sending these events to Matomo. */
  trackCartEvent(items: IOrderItem[], totalOrderValue: number): void {
    const aItems = this.analyticsMapping.mapOrderItemsToMatomoEcommerceItems(items);

    aItems.forEach((item: IMatomoEcommerceItem) => {
      if (item.quantity === 0) {
        this.callMatomo('removeEcommerceItem', { productSku: item.productSku });
      }

      if (item.quantity > 0) {
        this.callMatomo('addEcommerceItem', item);
      }
    });

    this.callMatomo('trackEcommerceCartUpdate', { totalValue: totalOrderValue });
  }

  /** On init of the confirmation page sends through the booking details and items in cart. */
  trackEcommerceOrder(order: IOrder): void {
    this.callMatomo('trackEcommerceOrder', <IMatomoEcommerceOrder>{
      orderId: order.draftBooking?.uniqueBookingId,
      grandTotal: order.amounts.total,
      subTotal: order.amounts.subTotal,
      tax: order.amounts.tax,
      shipping: order.amounts.postage,
      discount:
        order.discounts
          .map((disc: IDiscounts) => disc.discount)
          .reduce((sum: number, discount: number) => sum + discount, 0) || 0
    });
  }

  private callMatomo(event: TMatomoEvents, properties?: IDictionary<string | number | boolean>): Promise<void> | void {
    if (!this.matomo.isEnabled || !this.matomo.isScriptAdded || !this.window?._paq) {
      return;
    }

    return this.zone.runOutsideAngular(() => {
      try {
        const payload: (string | number | boolean)[] = [event];
        if (properties) {
          Object.values(properties).forEach((prop) => payload.push(prop));
        }

        return this.window?._paq.push(payload);
      } catch (error: unknown) {
        let errorMessage = 'Unknown error';
        if (error instanceof Error) {
          errorMessage = error.message;
        }

        this.loggerService.error(
          `Unable to push data to Matomo Analytics '${this.venueSlug} - ${this.checkoutSlug}' - siteId: '${this.matomoSite?.siteId}'`,
          {
            message: errorMessage
          }
        );

        this.matomo.isEnabled = false;
      }
    });
  }

  private resetEcommerceView() {
    this.callMatomo('setEcommerceView', {});
  }

  private getActionCustomDimensions(): IMatomoSetCustomDimension[] {
    const siteCustomDimensions = this.matomoSite?.customDimensions;
    const customDimensions: IMatomoSetCustomDimension[] = [];

    if (!siteCustomDimensions?.length) {
      return customDimensions;
    }

    const overlayCustomDimension = siteCustomDimensions.find((cd) => {
      return cd.name === 'Overlay' && cd.scope === 'action';
    });

    if (overlayCustomDimension) {
      customDimensions.push({ customDimensionId: overlayCustomDimension.id, customDimensionValue: this.isInIframe });
    }

    return customDimensions;
  }

  private loadMatomoScripts() {
    if (!this.matomoSite) {
      this.setInitialized(true);
      return;
    }

    const matomoAnalyticsUrl = this.appConfigService.getEnvValue<string>('matomoAnalyticsUrl');
    const matomoTrackerSrc = `//${matomoAnalyticsUrl}/matomo.js`;

    const customDimensionsScript = this.createMatomoCustomDimensionScript();
    const cookiePath = this.getCookiePath();

    const matomoInit = `
        var _paq = window._paq = window._paq || [];
        ${customDimensionsScript}
        _paq.push(['enableLinkTracking']);
        _paq.push(['AbTesting::enableDebugMode']);
        (function() {
          _paq.push(['setTrackerUrl', '//${matomoAnalyticsUrl}/matomo.php']);
          _paq.push(['setSiteId', '${this.matomoSite.siteId}']);
          _paq.push(['setCookiePath', '${cookiePath}']);
        })();
    `;

    this.scriptsService.addScriptElement(undefined, matomoInit, false, undefined, () => this.handleScriptLoadError());
    this.scriptsService.addScriptElement(
      matomoTrackerSrc,
      undefined,
      false,
      () => this.window?.matomoLoaded(),
      () => this.handleScriptLoadError()
    );
  }

  private getCookiePath() {
    return this.initialConfigService.getInitialConfig().isCustomDomain
      ? `/${this.checkoutSlug}/`
      : `/${this.venueSlug}/${this.checkoutSlug}/`;
  }

  private setMatomoSite(venueConfig: IVenueConfig) {
    this.matomoSite = venueConfig.analytics?.matomoSites?.[this.checkoutSlug];
  }

  private createMatomoCustomDimensionScript(): string {
    const actionCustomDimensions = this.getActionCustomDimensions();
    return actionCustomDimensions
      .map(
        ({ customDimensionId, customDimensionValue }) =>
          `_paq.push(['setCustomDimension', ${customDimensionId}, '${customDimensionValue}']);`
      )
      .join('\n');
  }

  private bootstrapAbTesting(venueConfig: IVenueConfig) {
    this.matomo.isEnabled = true;
    this.matomo.isScriptAdded = true;

    this.matomoAbTestingService.bootstrap(venueConfig, this.venueSlug);
    this.waitForAbTesting();
  }

  private waitForAbTesting() {
    this.matomoAbTestingService
      .getIsInitialized()
      .pipe(
        filter((isInitialized: boolean) => isInitialized),
        take(1),
        tap(() => this.setInitialized(true))
      )
      .subscribe();
  }

  private handleMatomoAnalyticsError(error: Error) {
    this.loggerService.error('Error configuring Matomo Analytics Service', error);
    this.setInitialized(true);
  }

  private handleScriptLoadError() {
    this.loggerService.error('Error loading Matomo scripts');
    this.setInitialized(true);
  }
}
